import {
    Charity,
    DistributionType,
    EstateFlowchartState,
    EstateItemVisualData,
    EstateToggle,
    FiduciaryMemberType,
    FiduciarySummary,
    FlowchartEstateItemType,
    FlowchartEstateType,
    FlowchartVisualDataAndDimensions,
    MemberType,
    Outright,
    RevocableTrust,
    Trust,
    Trustee
} from "../model/EstateFlowchartTypes";
import {
    EstateCardProps,
    EstateCardRole,
    EstateCardViewModeType,
    EstateTaxDueType
} from "../components/EstateCard/EstateCard";
import {Edge, Node} from "@xyflow/react";
import {FlowchartCustomEdgeProps} from "../components/FlowchartVisual/Edges/FlowchartCustomEdge";
import {MemberGroup} from "../../ClientManagement/models/InvestorGroupType";
import {DistributionsNodeProps} from "../components/FlowchartVisual/Nodes/DistributionsNode";
import {getMemberNameById, hasWillSummary, isNonTrustDistribution, isTrust} from "./DefUtils";
import {IrrevocableTrust} from "../model/IrrevocableTrust";
import {WillSummary} from "../model/WillSummary";
import {Will} from "../model/Will";
import {EstateTypeParentNodeProps} from "../components/FlowchartVisual/Nodes/EstateTypeParentNode";
import {FirstLevelParentNodeProps} from "../components/FlowchartVisual/Nodes/FirstLevelParentNode";
import React from "react";
import {Miscellaneous} from "../model/Miscellaneous";

// Flowchart Visual Constants
const IN_ESTATE_PARENT_NODE = "IN_ESTATE-parent-node";
const OUT_OF_ESTATE_PARENT_NODE = "OUT_OF_ESTATE-parent-node";

const DISTRIBUTIONS_NODE_MIN_HEIGHT = 48;
const EDGE_HEIGHT = 76;

const GRID_GAP_SIMPLE = 32;
const GRID_GAP_STANDARD = 24;

export const DISTRIBUTION_DIMENSIONS = {
    [EstateCardViewModeType.SIMPLE]: {
        DISTRIBUTIONS_NODE_MIN_WIDTH: 16,
        GRID_GAP: 32,
        FIRST_LEVEL_ESTATE_ITEM: {
            WIDTH: 272,
            HEIGHT: 132,
            HEIGHT_WITH_HEADER: 132 + 82, // 82 is the height of the header
        },
        NON_TRUST: {
            HEIGHT: 158,
            WIDTH: 272,
            WIDTH_WITH_GAP: 272 + GRID_GAP_SIMPLE,
        },
        TRUST: {
            WIDTH: 272,
            HEIGHT: 132,
            WIDTH_WITH_GAP: 272 + GRID_GAP_SIMPLE,
            HEIGHT_WITH_GAP: 132 + GRID_GAP_SIMPLE,
        },
    },
    [EstateCardViewModeType.STANDARD]: {
        DISTRIBUTIONS_NODE_MIN_WIDTH: 24,
        GRID_GAP: 24,
        FIRST_LEVEL_ESTATE_ITEM: {
            WIDTH: 398,
            HEIGHT: 158,
            HEIGHT_WITH_HEADER: 158 + 82, // 82 is the height of the header
        },
        NON_TRUST: {
            HEIGHT: 158,
            WIDTH: 398,
            WIDTH_WITH_GAP: 398 + GRID_GAP_STANDARD,
        },
        TRUST: {
            WIDTH: 398,
            HEIGHT: 246,
            WIDTH_WITH_GAP: 398 + GRID_GAP_STANDARD,
            HEIGHT_WITH_GAP: 246 + GRID_GAP_STANDARD,
            HEIGHT_WITH_HEADER: 246 + 82, // 82 is the height of the header
        },
    },
};

export type ParentEstateItemData = {
    id: string,
    type: FlowchartEstateItemType
}

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

const hasAdditionalDistributionsConfig = (
    enableAdditionalDistributionLayer: boolean | undefined,
    additionalDistributions: NonNullable<RevocableTrust["beneficiaries"]>["additionalDistributions"] | undefined
): boolean => {
    if (!enableAdditionalDistributionLayer || !additionalDistributions) return false;
    const {triggerEventType} = additionalDistributions;
    if (!triggerEventType) return false;

    switch (triggerEventType) {
        case "DEATH":
            return isNonEmptyString(additionalDistributions.triggerMemberId);
        case "AGE_ATTAINMENT":
            return (
                isNonEmptyString(additionalDistributions.triggerMemberId) &&
                additionalDistributions.triggerAge !== null &&
                additionalDistributions.triggerAge !== undefined
            );
        case "OTHER":
            return isNonEmptyString(additionalDistributions.triggerEventText);
        default:
            return false;
    }
}

const shouldShowAdditionalDistributionLayer = (
    showRemainderBeneficiaries: boolean | undefined,
    additionalDistributionsHasConfig: boolean
): boolean => Boolean(showRemainderBeneficiaries && additionalDistributionsHasConfig);

type AdditionalDistributionLayerContext = {
    nodes: Node[],
    edges: Edge[],
    estateItemVisualData: EstateItemVisualData,
    additionalDistributionEdgeId: string,
    remainderBeneficiariesLevelId: string,
    additionalDistributionLevelId: string,
    estateTypeParentNodeId: string,
    selectedCardRef?: React.RefObject<HTMLDivElement>,
    selectedCardId?: string,
}

const pushAdditionalDistributionLayerIfPresent = (ctx: AdditionalDistributionLayerContext): number => {
    let addedHeight = 0;

    if (ctx.estateItemVisualData.additionalDistributionEdge && ctx.estateItemVisualData.additionalDistributionLevel) {
        addedHeight += EDGE_HEIGHT;
        ctx.edges.push({
            id: ctx.additionalDistributionEdgeId,
            source: ctx.remainderBeneficiariesLevelId,
            target: ctx.additionalDistributionLevelId,
            type: 'customEdge',
            data: ctx.estateItemVisualData.additionalDistributionEdge,
            animated: false,
        });
    }

    if (ctx.estateItemVisualData.additionalDistributionLevel) {
        addedHeight += ctx.estateItemVisualData.additionalDistributionLevel.height;
        ctx.nodes.push({
            id: ctx.additionalDistributionLevelId,
            width: ctx.estateItemVisualData.additionalDistributionLevel.width,
            height: ctx.estateItemVisualData.additionalDistributionLevel.height,
            type: 'distributionsNode',
            position: {x: 0, y: 0},
            data: {
                ...ctx.estateItemVisualData.additionalDistributionLevel,
                selectedCardRef: ctx.selectedCardRef,
                selectedCardId: ctx.selectedCardId
            },
            parentId: ctx.estateTypeParentNodeId
        })
    }

    return addedHeight;
}

export const getEstateTypeNodes = (
    estateFlowchart: EstateFlowchartState,
    estateType: FlowchartEstateType,
    familyMembers: MemberGroup,
    fiduciarySummary?: FiduciarySummary,
    options?: {
        enableEstateFlowchartMVPRelease?: boolean,
        enableWildCardInEstateFlowchart?: boolean,
        enableAdditionalDistributionLayer?: boolean,
    },
    selectedCardRef?: React.RefObject<HTMLDivElement>,
    selectedCardId?: string
): FlowchartVisualDataAndDimensions => {
    const initialFlowchartVisualDataAndDimensions = getInitialNodesAndEdges(
        estateFlowchart,
        estateType,
        familyMembers,
        fiduciarySummary,
        options,
        selectedCardRef,
        selectedCardId
    );

    let estateTypeNodes: Node[] = [];

    const parentNodeTypes = [
        {id: IN_ESTATE_PARENT_NODE, header: "IN ESTATE"},
        {id: OUT_OF_ESTATE_PARENT_NODE, header: "OUT OF ESTATE"}
    ];

    const viewMode = estateFlowchart.viewMode ?? EstateCardViewModeType.SIMPLE;

    function calculateParentNodeWidth(nodes: Node[]): number {
        const firstLevelNodes = nodes.filter(node => node.type === "firstLevelParentNode");
        if (firstLevelNodes.length === 0) return nodes[0]?.width ?? 0;
        // Sum widths and add gap between, then remove last gap
        return firstLevelNodes.reduce((sum, node) => sum + (node.width ?? 0) + DISTRIBUTION_DIMENSIONS[viewMode].GRID_GAP, 0) - DISTRIBUTION_DIMENSIONS[viewMode].GRID_GAP;
    }

    parentNodeTypes.forEach(({id, header}) => {
        const nodes = initialFlowchartVisualDataAndDimensions.nodes.filter(node => node.parentId === id);
        if (nodes.length > 0) {
            const width = calculateParentNodeWidth(nodes);
            estateTypeNodes.push({
                id,
                type: 'estateTypeParentNode',
                position: {x: 0, y: 0},
                width,
                height: initialFlowchartVisualDataAndDimensions.flowchartHeight,
                data: {
                    headerLineText: header,
                } as EstateTypeParentNodeProps
            });
        }
    });

    return {
        nodes: [...estateTypeNodes, ...initialFlowchartVisualDataAndDimensions.nodes],
        edges: initialFlowchartVisualDataAndDimensions.edges,
        flowchartHeight: initialFlowchartVisualDataAndDimensions.flowchartHeight,
        flowchartWidth: initialFlowchartVisualDataAndDimensions.flowchartWidth
    };
}

export const getInitialNodesAndEdges = (
    estateFlowchart: EstateFlowchartState,
    estateType: FlowchartEstateType,
    familyMembers: MemberGroup,
    fiduciarySummary?: FiduciarySummary,
    options?: {
        enableEstateFlowchartMVPRelease?: boolean,
        enableWildCardInEstateFlowchart?: boolean,
        enableAdditionalDistributionLayer?: boolean,
    },
    selectedCardRef?: React.RefObject<HTMLDivElement>,
    selectedCardId?: string
): FlowchartVisualDataAndDimensions => {
    const viewMode = estateFlowchart.viewMode ?? EstateCardViewModeType.SIMPLE;
    let nodes: Node[] = [];
    let edges: Edge[] = [];

    if (!estateFlowchart) {
        return {flowchartHeight: 0, flowchartWidth: 0, nodes, edges};
    }

    const estateItemsVisualData: EstateItemVisualData[] = buildEstateItemsVisualData(
        estateFlowchart,
        estateType,
        familyMembers,
        fiduciarySummary,
        options?.enableEstateFlowchartMVPRelease,
        options?.enableWildCardInEstateFlowchart,
        options?.enableAdditionalDistributionLayer,
    );

    let flowchartHeight = 0, flowchartWidth = 0;

    estateItemsVisualData.forEach((estateItemVisualData, index) => {
        if (estateItemVisualData.firstLevelData?.firstLevelEstateCards?.length > 0) {
            let estateItemFlowchartHeight = estateItemVisualData.firstLevelData.height;
            let estateItemFlowchartWidth = estateItemVisualData.firstLevelData.width;

            const estateItemType = estateItemVisualData.estateItemType;
            const estateItemLevel1Id = `${estateItemType}-${index}`;
            const firstDeathBeneficiariesLevelId = `${estateItemLevel1Id}-firstDeathBeneficiaries-level`
            const firstDeathBeneficiariesEdgeId = `${estateItemLevel1Id}-firstDeathBeneficiaries-edge`
            const remainderBeneficiariesEdgeId = `${estateItemLevel1Id}-remainderBeneficiaries-edge`
            const remainderBeneficiariesLevelId = `${estateItemLevel1Id}-remainderBeneficiaries-level`
            const additionalDistributionLevelId = `${estateItemLevel1Id}-additionalDistribution-level`
            const additionalDistributionEdgeId = `${estateItemLevel1Id}-additionalDistribution-edge`

            const estateTypeParentNodeId = `${estateItemType}-parent-node`;

            const firstLevelParentNodeId = `${estateItemLevel1Id}-level1-parent-node`;
            nodes.push({
                    id: firstLevelParentNodeId,
                    width: estateItemVisualData.firstLevelData.width,
                    height: estateItemVisualData.firstLevelData.height,
                    type: 'firstLevelParentNode',
                    position: {x: 0, y: 0},
                    data: estateItemVisualData.firstLevelData,
                    parentId: estateTypeParentNodeId
                }
            );

            const childNodesEstateCardProps = estateItemVisualData.firstLevelData.firstLevelEstateCards;
            childNodesEstateCardProps.forEach((firstLevelCard) => {
                nodes.push({
                    id: `${firstLevelCard.estateItem.id}`,
                    width: DISTRIBUTION_DIMENSIONS[viewMode].FIRST_LEVEL_ESTATE_ITEM.WIDTH,
                    height: DISTRIBUTION_DIMENSIONS[viewMode].FIRST_LEVEL_ESTATE_ITEM.HEIGHT,
                    type: 'firstLevelNode',
                    position: {x: 0, y: 0},
                    data: {estateCardProps: firstLevelCard, selectedCardRef, selectCardId: selectedCardId},
                    parentId: firstLevelParentNodeId,
                });
            });

            if (childNodesEstateCardProps.length > 1) {
                edges.push(...buildWillAndRevTrustEdges(estateFlowchart.willSummary,
                    childNodesEstateCardProps,
                    estateFlowchart.revocableTrust
                ));
            }

            if (estateItemVisualData.firstDeathBeneficiariesLevel) {
                estateItemFlowchartHeight += estateItemVisualData.firstDeathBeneficiariesLevel.height;

                nodes.push({
                    id: firstDeathBeneficiariesLevelId,
                    width: estateItemVisualData.firstDeathBeneficiariesLevel.width,
                    height: estateItemVisualData.firstDeathBeneficiariesLevel.height,
                    type: 'distributionsNode',
                    position: {x: 0, y: 0},
                    data: {...estateItemVisualData.firstDeathBeneficiariesLevel, selectedCardRef, selectedCardId},
                    parentId: estateTypeParentNodeId
                })
            }

            if (estateItemVisualData.firstDeathBeneficiariesEdge) {
                estateItemFlowchartHeight += EDGE_HEIGHT; // Adding edge height to total height
                edges.push({
                    id: firstDeathBeneficiariesEdgeId,
                    source: firstLevelParentNodeId,
                    target: firstDeathBeneficiariesLevelId,
                    type: 'customEdge',
                    data: estateItemVisualData.firstDeathBeneficiariesEdge,
                    animated: false,
                });
            }

            if (estateItemVisualData.remainderBeneficiariesEdge) {
                estateItemFlowchartHeight += EDGE_HEIGHT; // Adding edge height to total height
                edges.push({
                    id: remainderBeneficiariesEdgeId,
                    source: estateItemVisualData.firstDeathBeneficiariesEdge ? firstDeathBeneficiariesLevelId : firstLevelParentNodeId,
                    target: remainderBeneficiariesLevelId,
                    type: 'customEdge',
                    data: estateItemVisualData.remainderBeneficiariesEdge,
                    animated: false,
                });
            }

            if (estateItemVisualData.remainderBeneficiariesLevel) {
                estateItemFlowchartHeight += estateItemVisualData.remainderBeneficiariesLevel.height;

                nodes.push({
                    id: remainderBeneficiariesLevelId,
                    width: estateItemVisualData.remainderBeneficiariesLevel.width,
                    height: estateItemVisualData.remainderBeneficiariesLevel.height,
                    type: 'distributionsNode',
                    position: {x: 0, y: 0},
                    data: {...estateItemVisualData.remainderBeneficiariesLevel, selectedCardRef, selectedCardId},
                    parentId: estateTypeParentNodeId
                })
            }

            estateItemFlowchartHeight += pushAdditionalDistributionLayerIfPresent({
                nodes,
                edges,
                estateItemVisualData,
                additionalDistributionEdgeId,
                remainderBeneficiariesLevelId,
                additionalDistributionLevelId,
                estateTypeParentNodeId,
                selectedCardRef,
                selectedCardId
            });



            flowchartHeight = Math.max(flowchartHeight, estateItemFlowchartHeight);
            flowchartWidth += estateItemFlowchartWidth;
        }
    });

    return {nodes, edges, flowchartHeight, flowchartWidth};
}

const addWillCard = (will: Will | undefined,
                     viewMode: EstateCardViewModeType,
                     roles: EstateCardRole[]): EstateCardProps => {
    return will ? {
        viewMode,
        sequenceNumber: 1,
        estateItem: {
            parentEstateItemId: will.trustId,
            parentEstateItemType: FlowchartEstateItemType.WILL,
            id: will.trustId,
            title: will.nickName,
            distributionType: DistributionType.WILL,
            roles: roles,
        }
    } : {
        viewMode,
        sequenceNumber: 1,
        estateItem: {
            parentEstateItemId: "placeHolder-parent-trust-id",
            parentEstateItemType: FlowchartEstateItemType.WILL,
            id: "placeHolder-card-id",
            title: "placeHolder-title",
            distributionType: DistributionType.WILL,
            roles: [],
        }
    };
}

export function createRevocableTrustCard(trust: RevocableTrust, viewMode: EstateCardViewModeType, familyMembers: MemberGroup): EstateCardProps {
    return {
        viewMode,
        sequenceNumber: 1,
        estateItem: {
            parentEstateItemId: trust.trustId,
            parentEstateItemType: FlowchartEstateItemType.REVOCABLE,
            id: trust.trustId,
            title: trust.nickName,
            distributionType: DistributionType.IN_TRUST,
            roles: [{
                type: MemberType.Grantor,
                value: trust.grantors.map(
                    member => member.familyMember ? getMemberNameById(familyMembers, member.memberId!) : member.customName
                ).join(', ') ?? ""
            }, {
                type: MemberType.Trustee,
                value: trust.trustees.map(
                    member => member.familyMember ? getMemberNameById(familyMembers, member.memberId!) : member.customName
                ).join(', ') ?? ""
            }],
        }
    };
}

export const buildWillAndRevTrustEdges = (
    willSummary: WillSummary | undefined,
    firstLevelNodeCardProps: EstateCardProps[],
    revocableTrust: RevocableTrust | undefined | null,
): Edge[] => {
    let willEdges: Edge[] = []

    for (let i = 0; i < firstLevelNodeCardProps.length - 1; i++) {
        willEdges.push({
            id: `will-edge-${i}`,
            source: `${firstLevelNodeCardProps[i].estateItem.id}`,
            target: `${firstLevelNodeCardProps[i + 1].estateItem.id}`,
            type: 'straight',
            style: {strokeWidth: 2, color: "#cecece", opacity: 0},
            data: {},
            animated: false,
        });
    }

    if (revocableTrust && willSummary) {
        [willSummary.primaryWill, willSummary.partnerWill].forEach((will) => {
            if (
                will &&
                will.pourOverWill &&
                revocableTrust.grantors.some(grantor => grantor.memberId === will.willOwner)
            ) {
                willEdges.forEach(edge => {
                    if (edge.source === will.trustId || edge.target === will.trustId) {
                        edge.style = {strokeWidth: 2, color: "#cecece", opacity: 1};
                    }
                });
            }
        });
    }

    return willEdges;
}

export const buildFirstLevelParentNodeData = (
    estateFlowchart: EstateFlowchartState,
    estateItemType: FlowchartEstateType,
    familyMembers: MemberGroup,
    fiduciarySummary?: FiduciarySummary,
    enableEstateFlowchartMVPRelease?: boolean,
): FirstLevelParentNodeProps => {
    const viewMode = estateFlowchart.viewMode ?? EstateCardViewModeType.SIMPLE;
    const willSummary = estateFlowchart.willSummary;
    const revocableTrust = estateFlowchart.revocableTrust ?? undefined;
    const irrevocableTrust = (estateFlowchart.irrevocableTrusts && estateFlowchart.irrevocableTrusts.length > 0) ? estateFlowchart.irrevocableTrusts[0] : undefined;
    const wildcardTrust = (estateFlowchart.wildcardTrusts && estateFlowchart.wildcardTrusts.length > 0) ? estateFlowchart.wildcardTrusts[0] : undefined;

    let headerLineText = "IN ESTATE";
    const firstLevelCards: EstateCardProps[] = [];
    const {primaryWill, partnerWill} = willSummary ?? {};
    const primaryWillCardRoles = primaryWill ? getWillFiduciaryRoles(primaryWill, familyMembers, fiduciarySummary) : [];
    const partnerWillCardRoles = partnerWill ? getWillFiduciaryRoles(partnerWill, familyMembers, fiduciarySummary) : [];

    function calculateInEstateParentNodeWidth(
        viewMode: EstateCardViewModeType,
        revocableTrust?: RevocableTrust,
        primaryWill?: Will,
        partnerWill?: Will
    ): number {
        let width = DISTRIBUTION_DIMENSIONS[viewMode].TRUST.WIDTH_WITH_GAP;
        if (revocableTrust && (primaryWill || partnerWill)) {
            width *= 3;
        } else if (!revocableTrust && primaryWill && partnerWill) {
            width += DISTRIBUTION_DIMENSIONS[viewMode].TRUST.WIDTH_WITH_GAP;
        }
        return width - DISTRIBUTION_DIMENSIONS[viewMode].GRID_GAP;
    }

    function addSingleWillCard() {
        if (primaryWill) {
            firstLevelCards.push(addWillCard(primaryWill, viewMode, primaryWillCardRoles));
        }
        if (!primaryWill && partnerWill) {
            firstLevelCards.push(addWillCard(partnerWill, viewMode, partnerWillCardRoles));
        }
    }

    function addRevocableTrustCards() {
        if (revocableTrust) {
            firstLevelCards.push(createRevocableTrustCard(revocableTrust, viewMode, familyMembers));
            if (primaryWill && partnerWill) {
                firstLevelCards.push(addWillCard(partnerWill, viewMode, partnerWillCardRoles));
            } else if (primaryWill || partnerWill) {
                firstLevelCards.push(addWillCard(undefined, viewMode, []));
            }
        } else if (primaryWill && partnerWill) {
            firstLevelCards.push(addWillCard(partnerWill, viewMode, partnerWillCardRoles));
        }
    }

    function addMiscCard(miscTrust: Miscellaneous | undefined) {
        if (miscTrust) {
            firstLevelCards.push({
                viewMode,
                sequenceNumber: 1,
                estateItem: {
                    parentEstateItemId: miscTrust.trustId,
                    parentEstateItemType: FlowchartEstateItemType.MISCELLANEOUS,
                    id: miscTrust.trustId,
                    title: miscTrust.name,
                    distributionType: getDistributionTypeFromVisualCardRepresentation(miscTrust.visualCardRepresentation),
                    roles: Array.isArray(miscTrust.beneficiaries) && miscTrust.beneficiaries.length > 0 ? [{
                        type: MemberType.Beneficiary,
                        value: miscTrust.beneficiaries.map(
                            member => member.familyMember ? getMemberNameById(familyMembers, member.memberId!) : member.customName
                        ).join(', ') ?? ""
                    }] : []
                }
            });
        }
    }

    function buildOutOfEstate() {
        headerLineText = "OUT OF ESTATE";
        if (irrevocableTrust) {
            firstLevelCards.push({
                viewMode,
                sequenceNumber: 1,
                estateItem: {
                    parentEstateItemId: irrevocableTrust.trustId,
                    parentEstateItemType: FlowchartEstateItemType.IRREVOCABLE,
                    id: irrevocableTrust.trustId,
                    title: irrevocableTrust.nickName,
                    distributionType: DistributionType.IN_TRUST,
                    roles: [
                        {
                            type: MemberType.Grantor,
                            value: irrevocableTrust.grantors.map(
                                member => member.familyMember ? getMemberNameById(familyMembers, member.memberId!) : member.customName
                            ).join(', ') ?? ""
                        },
                        {
                            type: MemberType.Trustee,
                            value: irrevocableTrust.trustees.map(
                                member => member.familyMember ? getMemberNameById(familyMembers, member.memberId!) : member.customName
                            ).join(', ') ?? ""
                        }
                    ],
                    tag: enableEstateFlowchartMVPRelease ? irrevocableTrust.tags : undefined,
                }
            });
        } else if (wildcardTrust) {
            addMiscCard(wildcardTrust);
        }
        return {
            firstLevelEstateCards: firstLevelCards,
            height: DISTRIBUTION_DIMENSIONS[viewMode].FIRST_LEVEL_ESTATE_ITEM.HEIGHT_WITH_HEADER,
            width: DISTRIBUTION_DIMENSIONS[viewMode].TRUST.WIDTH,
            headerLineText,
        };
    }

    function buildInEstate() {
        const inEstateParentNodeWidth = calculateInEstateParentNodeWidth(viewMode, revocableTrust, primaryWill, partnerWill);
        addSingleWillCard();
        addRevocableTrustCards();
        return {
            firstLevelEstateCards: firstLevelCards,
            height: DISTRIBUTION_DIMENSIONS[viewMode].FIRST_LEVEL_ESTATE_ITEM.HEIGHT_WITH_HEADER,
            width: inEstateParentNodeWidth,
            headerLineText,
        };
    }

    function buildInEstateMisc() {
        addMiscCard(wildcardTrust);
        return {
            firstLevelEstateCards: firstLevelCards,
            height: DISTRIBUTION_DIMENSIONS[viewMode].FIRST_LEVEL_ESTATE_ITEM.HEIGHT_WITH_HEADER,
            width: DISTRIBUTION_DIMENSIONS[viewMode].TRUST.WIDTH,
            headerLineText,
        };
    }

    if (estateItemType === FlowchartEstateType.OUT_OF_ESTATE && (irrevocableTrust || wildcardTrust)) {
        return buildOutOfEstate();
    }
    if (estateItemType === FlowchartEstateType.IN_ESTATE && (revocableTrust || primaryWill || partnerWill)) {
        return buildInEstate();
    } else if (estateItemType === FlowchartEstateType.IN_ESTATE && wildcardTrust) {
        return buildInEstateMisc();
    }
    return {} as FirstLevelParentNodeProps;
}

/**
 * Generates flowchart data for a given estate flowchart state, estate type, and family members.
 * Determines the appropriate flowchart structure based on the estate type and whether a revocable trust exists.
 *
 * @param estateFlowchart - The current state of the estate flowchart, which includes details about the revocable trust and other estate-related data.
 * @param flowchartEstateType - Specifies the type of estate flowchart to build. Possible values include:
 *                              - `FlowchartEstateType.IN_ESTATE`: Indicates the estate is in trust.
 * @param familyMembers - A group of family members involved in the estate, including primary, partner, and additional members.
 * @param fiduciarySummary
 * @param enableEstateFlowchartMVPRelease
 * @param enableWildCardInEstateFlowchart
 * @param enableAdditionalDistributionLayer
 * @returns The constructed flowchart data if the estate type is `IN_ESTATE` and a revocable trust exists. Otherwise, returns `undefined`.
 *
 * Behavior:
 * - If the `flowchartEstateType` is `FlowchartEstateType.IN_ESTATE` and the `estateFlowchart` contains a `revocableTrust`,
 *   the method delegates the construction of flowchart data to the `buildRevocableTrustData` method.
 */
export const buildEstateItemsVisualData = (
    estateFlowchart: EstateFlowchartState,
    flowchartEstateType: FlowchartEstateType,
    familyMembers: MemberGroup,
    fiduciarySummary?: FiduciarySummary,
    enableEstateFlowchartMVPRelease?: boolean,
    enableWildCardInEstateFlowchart?: boolean,
    enableAdditionalDistributionLayer?: boolean,
): EstateItemVisualData[] => {
    let viewMode = estateFlowchart.viewMode ?? EstateCardViewModeType.SIMPLE;

    const builtEstateItemsVisualData: EstateItemVisualData[] = [];
    const buildInEstateVisualData = () => {
        if (estateFlowchart.revocableTrust) {
            const builtRevocableTrust = buildRevocableTrustData(
                estateFlowchart,
                viewMode,
                estateFlowchart.estateTaxDueTypes ?? [],
                familyMembers,
                fiduciarySummary,
                enableEstateFlowchartMVPRelease,
                enableAdditionalDistributionLayer);
            builtEstateItemsVisualData.push(builtRevocableTrust);
        } else if (hasWillSummary(estateFlowchart.willSummary).hasWills) {
            const estateFlowchartForFirstLevelParentNode: EstateFlowchartState = {
                ...estateFlowchart,
                revocableTrust: undefined,
                irrevocableTrusts: undefined,
                wildcardTrusts: []
            }
            builtEstateItemsVisualData.push({
                firstLevelData: buildFirstLevelParentNodeData(
                    estateFlowchartForFirstLevelParentNode,
                    FlowchartEstateType.IN_ESTATE,
                    familyMembers,
                    fiduciarySummary,
                    enableEstateFlowchartMVPRelease
                ),
                estateItemType: FlowchartEstateType.IN_ESTATE
            });
        }
        if (enableWildCardInEstateFlowchart) {
            builtEstateItemsVisualData.push(
                ...buildMiscellaneousTrustData(
                    estateFlowchart,
                    estateFlowchart.wildcardTrusts,
                    FlowchartEstateType.IN_ESTATE,
                    familyMembers,
                    fiduciarySummary,
                    enableEstateFlowchartMVPRelease
                )
            );
        }
    }

    const buildOutOfEstateVisualData = () => {
        if (estateFlowchart.irrevocableTrusts && estateFlowchart.irrevocableTrusts.length > 0) {
            estateFlowchart.irrevocableTrusts.forEach(irrevocableTrust => {
                builtEstateItemsVisualData.push(buildIrrevocableTrustData(estateFlowchart, irrevocableTrust, viewMode, familyMembers, enableEstateFlowchartMVPRelease));
            });
        }
        if (enableWildCardInEstateFlowchart) {
            builtEstateItemsVisualData.push(
                ...buildMiscellaneousTrustData(
                    estateFlowchart,
                    estateFlowchart.wildcardTrusts,
                    FlowchartEstateType.OUT_OF_ESTATE,
                    familyMembers,
                    fiduciarySummary,
                    enableEstateFlowchartMVPRelease
                )
            );
        }
    }

    if (flowchartEstateType === FlowchartEstateType.IN_ESTATE) {
        buildInEstateVisualData();
    } else if (flowchartEstateType === FlowchartEstateType.OUT_OF_ESTATE) {
        buildOutOfEstateVisualData();
    } else if (flowchartEstateType === FlowchartEstateType.FULL_ESTATE) {
        buildInEstateVisualData();
        buildOutOfEstateVisualData();
    }

    return builtEstateItemsVisualData;
}

/**
 * Constructs flowchart data for a revocable trust, including its beneficiaries and distributions.
 * This method calculates the dimensions and structure of the flowchart based on the trust's data.
 *
 * @param estateFlowchart
 * @param viewMode - Specifies the view mode for the estate cards (e.g., SIMPLE or DETAILED).
 * @param familyMembers - A group of family members involved in the estate, including primary, partner, and additional members.
 * @param fiduciarySummary
 * @param estateTaxDueTypes - An array of estate tax due types to determine if tax due pills should be displayed on edges.
 * @param enableEstateFlowchartMVPRelease
 * @param enableAdditionalDistributionLayer
 * @returns The constructed flowchart data, including first-level data, edge data, and second-level data.
 */
export const buildRevocableTrustData = (
    estateFlowchart: EstateFlowchartState,
    viewMode: EstateCardViewModeType,
    estateTaxDueTypes: EstateTaxDueType[],
    familyMembers: MemberGroup,
    fiduciarySummary?: FiduciarySummary,
    enableEstateFlowchartMVPRelease?: boolean,
    enableAdditionalDistributionLayer?: boolean,
): EstateItemVisualData => {
    const flowchartForFirstLevel: EstateFlowchartState = {
        ...estateFlowchart,
        irrevocableTrusts: undefined,
        wildcardTrusts: []
    };
    const revocableTrust = estateFlowchart.revocableTrust!;

    const hasAnyDistributions = (data?: DistributionsNodeProps): boolean => Boolean(
        data && (data.nonTrustDistributions.length > 0 || data.trustDistributions.length > 0)
    );

    const firstLevelData = buildFirstLevelParentNodeData(
        flowchartForFirstLevel,
        FlowchartEstateType.IN_ESTATE,
        familyMembers,
        fiduciarySummary,
        enableEstateFlowchartMVPRelease
    );

    const firstDeathBeneficiaries = (revocableTrust.beneficiaries && revocableTrust.hasBeneficiariesAtFirstDeath) ?
        getBeneficiariesDistributionsData(revocableTrust.beneficiaries.firstDeathDistributions, familyMembers, viewMode, {
            id: revocableTrust.trustId,
            type: FlowchartEstateItemType.REVOCABLE
        }, enableEstateFlowchartMVPRelease) : undefined;

    const showFirstDeathBeneficiaries = hasAnyDistributions(firstDeathBeneficiaries);

    const remainderBeneficiariesEdgeMemberId = getRemainderBeneficiariesEdgeMemberId(revocableTrust.hasBeneficiariesAtFirstDeath,
        revocableTrust.assumedFirstDeath, familyMembers);

    const remainderBeneficiaries = revocableTrust.beneficiaries ?
        getBeneficiariesDistributionsData(revocableTrust.beneficiaries.remainderDistributions, familyMembers, viewMode, {
            id: revocableTrust.trustId,
            type: FlowchartEstateItemType.REVOCABLE
        }, enableEstateFlowchartMVPRelease) : undefined;

    const showRemainderBeneficiaries = hasAnyDistributions(remainderBeneficiaries);

    const additionalDistributions = enableAdditionalDistributionLayer
        ? revocableTrust.beneficiaries?.additionalDistributions
        : undefined;
    const additionalDistributionsHasConfig = hasAdditionalDistributionsConfig(
        enableAdditionalDistributionLayer,
        additionalDistributions
    );

    const showAdditionalDistributionLayer = shouldShowAdditionalDistributionLayer(
        showRemainderBeneficiaries,
        additionalDistributionsHasConfig
    );

    const additionalDistributionsData = showAdditionalDistributionLayer
        ? getBeneficiariesDistributionsData(
            additionalDistributions?.distributions ?? [],
            familyMembers,
            viewMode,
            {
                id: revocableTrust.trustId,
                type: FlowchartEstateItemType.REVOCABLE
            },
            enableEstateFlowchartMVPRelease
        )
        : undefined;

    const showAdditionalDistributionNode =
        showAdditionalDistributionLayer && hasAnyDistributions(additionalDistributionsData);

    const maxWidth = Math.max(
        firstDeathBeneficiaries?.width ?? 0,
        remainderBeneficiaries?.width ?? 0,
        additionalDistributionsData?.width ?? 0,
        firstLevelData.width
    );

    return {
        estateItemType: FlowchartEstateType.IN_ESTATE,
        firstLevelData: {
            ...firstLevelData,
            width: maxWidth
        },
        firstDeathBeneficiariesEdge: (showFirstDeathBeneficiaries && revocableTrust.assumedFirstDeath) ? {
            memberId: revocableTrust.assumedFirstDeath,
            width: maxWidth,
            shouldShowEstateTaxDuePill: estateTaxDueTypes.includes(EstateTaxDueType.FIRST_DEATH)
        } as FlowchartCustomEdgeProps : undefined,
        firstDeathBeneficiariesLevel: showFirstDeathBeneficiaries ? {
            height: firstDeathBeneficiaries!.height,
            width: maxWidth,
            nonTrustDistributions: firstDeathBeneficiaries!.nonTrustDistributions,
            trustDistributions: firstDeathBeneficiaries!.trustDistributions,
            showBottomBracket: true,
            viewMode
        } : undefined,
        remainderBeneficiariesEdge: (showRemainderBeneficiaries && remainderBeneficiariesEdgeMemberId) ? {
            memberId: remainderBeneficiariesEdgeMemberId,
            width: maxWidth,
            shouldShowEstateTaxDuePill: estateTaxDueTypes.includes(EstateTaxDueType.SECOND_DEATH)
        } : undefined,
        remainderBeneficiariesLevel: showRemainderBeneficiaries ? {
            height: remainderBeneficiaries!.height,
            width: maxWidth,
            nonTrustDistributions: remainderBeneficiaries!.nonTrustDistributions,
            trustDistributions: remainderBeneficiaries!.trustDistributions,
            showBottomBracket: showAdditionalDistributionNode,
            viewMode
        } : undefined,

        additionalDistributionEdge: showAdditionalDistributionNode ? {
            memberId: remainderBeneficiariesEdgeMemberId,
            width: maxWidth,
            additionalDistribution: additionalDistributions,
        } : undefined,
        additionalDistributionLevel: showAdditionalDistributionNode && additionalDistributionsData ? {
            height: additionalDistributionsData.height,
            width: maxWidth,
            nonTrustDistributions: additionalDistributionsData.nonTrustDistributions,
            trustDistributions: additionalDistributionsData.trustDistributions,
            viewMode
        } : undefined,
    };
}

export const buildIrrevocableTrustData = (
    estateFlowchart: EstateFlowchartState,
    irrevocableTrust: IrrevocableTrust,
    viewMode: EstateCardViewModeType,
    familyMembers: MemberGroup,
    enableEstateFlowchartMVPRelease?: boolean
): EstateItemVisualData => {
    // Remove revocableTrusts, wills and other irrevocableTrusts for first level node data
    const flowchartForFirstLevel: EstateFlowchartState = {
        ...estateFlowchart,
        revocableTrust: undefined,
        irrevocableTrusts: [irrevocableTrust],
        willSummary: undefined,
        wildcardTrusts: []
    };

    const firstLevelData = buildFirstLevelParentNodeData(
        flowchartForFirstLevel,
        FlowchartEstateType.OUT_OF_ESTATE,
        familyMembers,
        undefined,
        enableEstateFlowchartMVPRelease
    );

    const firstDeathBeneficiaries = (irrevocableTrust.firstDeathBeneficiaries && irrevocableTrust.terminatesUponSpecificDeath) ?
        getBeneficiariesDistributionsData(irrevocableTrust.firstDeathBeneficiaries.firstDeathDistributions, familyMembers, viewMode, {
            id: irrevocableTrust.trustId,
            type: FlowchartEstateItemType.IRREVOCABLE
        }, enableEstateFlowchartMVPRelease) : undefined;

    const showFirstDeathBeneficiaries = firstDeathBeneficiaries &&
        (firstDeathBeneficiaries.nonTrustDistributions.length > 0 || firstDeathBeneficiaries.trustDistributions.length > 0)

    // Find max width of all 3 levels, 272 being default width for first level
    const maxWidth = Math.max(
        firstDeathBeneficiaries?.width ?? 0,
        firstLevelData.width
    );

    return {
        estateItemType: FlowchartEstateType.OUT_OF_ESTATE,
        firstLevelData: {
            ...firstLevelData,
            width: maxWidth,
        },
        firstDeathBeneficiariesEdge: (showFirstDeathBeneficiaries && irrevocableTrust.firstDeathTerminationMember) ? {
            memberId: irrevocableTrust.firstDeathTerminationMember.memberId,
            width: maxWidth,
        } as FlowchartCustomEdgeProps : undefined,
        firstDeathBeneficiariesLevel: showFirstDeathBeneficiaries ? {
            height: firstDeathBeneficiaries.height,
            width: maxWidth,
            nonTrustDistributions: firstDeathBeneficiaries.nonTrustDistributions,
            trustDistributions: firstDeathBeneficiaries.trustDistributions,
            viewMode,
            showBottomBracket: false
        } : undefined,
    };
}

function buildMiscellaneousTrustData(
    estateFlowchart: EstateFlowchartState,
    wildcardTrusts: Miscellaneous[] | undefined,
    estateType: FlowchartEstateType,
    familyMembers: MemberGroup,
    fiduciarySummary?: FiduciarySummary,
    enableEstateFlowchartMVPRelease?: boolean
): EstateItemVisualData[] {
    if (!wildcardTrusts || wildcardTrusts.length === 0) return [];
    const toggleType = estateType === FlowchartEstateType.IN_ESTATE ? EstateToggle.IN_ESTATE : EstateToggle.OUT_OF_ESTATE;
    const trustsByToggle = wildcardTrusts.filter(trust => trust.estateToggleType === toggleType);
    return trustsByToggle.map(miscTrust => {
        const flowchartForWildcardTrust: EstateFlowchartState = {
            ...estateFlowchart,
            revocableTrust: undefined,
            irrevocableTrusts: [],
            willSummary: undefined,
            wildcardTrusts: [miscTrust]
        };
        return {
            firstLevelData: buildFirstLevelParentNodeData(
                flowchartForWildcardTrust,
                estateType,
                familyMembers,
                fiduciarySummary,
                enableEstateFlowchartMVPRelease
            ),
            estateItemType: estateType
        };
    });
}

const getRemainderBeneficiariesEdgeMemberId = (hasFirstDeathBeneficiaries: boolean, assumedFirstDeath: string, familyMembers: MemberGroup) => {
    if (!hasFirstDeathBeneficiaries && assumedFirstDeath) {
        return assumedFirstDeath;
    }
    return assumedFirstDeath === familyMembers.primaryMember?.id ? (familyMembers.partnerMember?.id ?? "partnerMember") : familyMembers.primaryMember.id;
}

/**
 * Calculates the dimensions and structure of the beneficiaries' distributions node
 * for a revocable trust. This includes both trust and non-trust distributions.
 *
 * The method determines the height and width of the node based on the number
 * and type of distributions provided. It also generates the data for each
 * distribution to be displayed in the flowchart.
 *
 * @param distributions - An array of distributions, which can include charities, trusts, or outright distributions.
 * @param familyMembers - A group of family members involved in the estate, including primary, partner, and additional members.
 * @param viewMode - Specifies the view mode for the estate cards (e.g., SIMPLE or DETAILED).
 * @param parentEstateData - contains information from the first Level - id and type of estate
 * @param enableEstateFlowchartMVPRelease
 * @returns An object containing the calculated height, width, and data for non-trust and trust distributions.
 */
const getBeneficiariesDistributionsData = (distributions: (Trust | Charity | Outright)[],
                                           familyMembers: MemberGroup,
                                           viewMode: EstateCardViewModeType,
                                           parentEstateData: ParentEstateItemData,
                                           enableEstateFlowchartMVPRelease?: boolean): DistributionsNodeProps => {
    const dimensionsByViewMode = DISTRIBUTION_DIMENSIONS[viewMode];

    let nodeHeight = DISTRIBUTIONS_NODE_MIN_HEIGHT;
    let nodeWidth = enableEstateFlowchartMVPRelease ? dimensionsByViewMode.DISTRIBUTIONS_NODE_MIN_WIDTH : GRID_GAP_STANDARD;

    const nonTrustDistributionsData = getNonTrustDistributions(
        distributions.filter(distribution => isNonTrustDistribution(distribution.distributionType)),
        familyMembers,
        viewMode,
        parentEstateData
    );

    if (nonTrustDistributionsData.length > 0) {
        nodeHeight += dimensionsByViewMode.NON_TRUST.HEIGHT;
        nodeWidth += dimensionsByViewMode.NON_TRUST.WIDTH_WITH_GAP * nonTrustDistributionsData.length;
    }

    const trustDistributionsData = getTrustDistributions(
        distributions.filter(distribution => isTrust(distribution.distributionType)) as Trust[],
        familyMembers,
        viewMode,
        parentEstateData
    );

    if (trustDistributionsData.length > 0) {
        if (enableEstateFlowchartMVPRelease) {
            nodeWidth = Math.max(nodeWidth, dimensionsByViewMode.TRUST.WIDTH_WITH_GAP
                * trustDistributionsData.length
                + dimensionsByViewMode.DISTRIBUTIONS_NODE_MIN_WIDTH);
        } else {
            nodeWidth = Math.max(nodeWidth, dimensionsByViewMode.TRUST.WIDTH_WITH_GAP
                * trustDistributionsData.length
                + dimensionsByViewMode.GRID_GAP);
        }
        nodeHeight += dimensionsByViewMode.TRUST.HEIGHT_WITH_GAP * Math.max(...trustDistributionsData.map(group => group.length));
        if (nonTrustDistributionsData.length === 0) {
            nodeHeight -= dimensionsByViewMode.GRID_GAP;
        }
    }

    return {
        viewMode,
        height: nodeHeight,
        width: nodeWidth,
        nonTrustDistributions: [...nonTrustDistributionsData],
        trustDistributions: [...trustDistributionsData]
    };
}

/**
 * Generates estate card properties for non-trust distributions, including charities and outright distributions.
 * This method processes the given distributions and calculates the data required for rendering estate cards
 * in the flowchart. It handles both charity and outright distribution types.
 *
 * @param distributions - An array of distributions, which can include charities or outright distributions.
 * @param familyMembers - A group of family members involved in the estate, including primary, partner, and additional members.
 * @param viewMode - Specifies the view mode for the estate cards (e.g., SIMPLE or DETAILED).
 * @param parentEstateData - contains information from the first Level - id and type of estate
 * @returns An array of estate card properties for the given non-trust distributions.
 */
export const getNonTrustDistributions = (distributions: (Charity | Outright)[],
                                         familyMembers: MemberGroup,
                                         viewMode: EstateCardViewModeType,
                                         parentEstateData: ParentEstateItemData): EstateCardProps[] => {
    const sortedDistributions = [...distributions].sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0));

    const allCharities = sortedDistributions.filter(
        (beneficiary) => beneficiary.distributionType === DistributionType.CHARITY
    ) || [];

    const allOutrightDistributions = sortedDistributions.filter(
        (beneficiary) => beneficiary.distributionType === DistributionType.OUTRIGHT
    ) || [];

    let allCharitiesEstateCardProps: EstateCardProps[] = [];
    allCharities.forEach((charity) => (
        allCharitiesEstateCardProps.push({
            viewMode: viewMode,
            sequenceNumber: charity.sequenceNumber ?? 0,
            estateItem: {
                parentEstateItemId: parentEstateData.id,
                parentEstateItemType: parentEstateData.type,
                id: charity.distributionId,
                title: "Charity",
                distributionType: DistributionType.CHARITY,
                roles: [{
                    type: MemberType.Beneficiary,
                    value: (charity as Charity).charityBeneficiaries?.length
                        ? (charity as Charity).charityBeneficiaries.map(charityBeneficiary => charityBeneficiary.name).join(', ')
                        : charity.name ?? ""
                }],
            },
        }))
    );

    let allOutrightEstateCardProps: EstateCardProps[] = [];
    allOutrightDistributions.forEach((outright) => (
        allOutrightEstateCardProps.push({
            viewMode: viewMode,
            sequenceNumber: outright.sequenceNumber ?? 0,
            estateItem: {
                parentEstateItemId: parentEstateData.id,
                parentEstateItemType: parentEstateData.type,
                id: outright.distributionId,
                title: "Outright",
                distributionType: DistributionType.OUTRIGHT,
                roles: [{
                    type: MemberType.Beneficiary,
                    value: (outright as Outright).outrightMembers?.map(
                        member => member.familyMember ? getMemberNameById(familyMembers, member.memberId!) : member.customName
                    ).join(', ') ?? ""
                }],
            },
        }))
    );

    return [...allOutrightEstateCardProps, ...allCharitiesEstateCardProps];
}

/**
 * Generates estate card properties for trust distributions grouped by type.
 * This method processes the given trust distributions, sorts them by sequence number,
 * and groups them by distribution type (e.g., MARITAL, CREDIT_SHELTER, SURVIVOR).
 * Each group is then converted into estate card properties for rendering in the flowchart.
 *
 * @param distributions - An array of trust distributions to process.
 * @param familyMembers - A group of family members involved in the estate, including primary, partner, and additional members.
 * @param viewMode - Specifies the view mode for the estate cards (e.g., SIMPLE or DETAILED).
 * @param parentEstateData - contains information from the first Level - id and type of estate
 * @returns A 2D array of estate card properties grouped by distribution type, sorted by sequence number.
 */
export const getTrustDistributions = (distributions: Trust[],
                                      familyMembers: MemberGroup,
                                      viewMode: EstateCardViewModeType,
                                      parentEstateData: ParentEstateItemData): EstateCardProps[][] => {
    const sortedDistributions = [...distributions].sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0));

    let groupedDistributions: Trust[][] = [DistributionType.MARITAL, DistributionType.CREDIT_SHELTER, DistributionType.SURVIVOR]
        .map(type => sortedDistributions.filter(trust => trust.distributionType === DistributionType[type]));

    const groupedIntrustDistributions = getInTrustDistributions(sortedDistributions.filter(trust => trust.distributionType === DistributionType.IN_TRUST));
    groupedDistributions = [...groupedDistributions, ...groupedIntrustDistributions]

    return groupedDistributions
        .filter(group => group.length > 0)
        .map(group => group.map(trust => ({
            viewMode: viewMode,
            sequenceNumber: trust.sequenceNumber ?? 0,
            estateItem: {
                parentEstateItemId: parentEstateData.id,
                parentEstateItemType: parentEstateData.type,
                id: trust.distributionId,
                title: trust.name ?? "",
                distributionType: trust.distributionType,
                roles: getTrustDistributionRoles(trust.trustees, trust.beneficiaries, familyMembers),
                tag: trust.tags,
                body: {header: "Distributions", content: trust.distributions ?? ""},
            }
        })))
        .sort((a, b) => (a[0]?.sequenceNumber ?? 0) - (b[0]?.sequenceNumber ?? 0));
};

export const getInTrustDistributions = (inTrustDistributions: Trust[]): Trust[][] => {
    const groupedInTrustDistributions: Trust[][] = [];
    const singleBeneficiaryDistributions: Map<string, Trust[]> = new Map();

    inTrustDistributions.forEach((trust) => {
        const beneficiary = trust.beneficiaries[0];
        if (trust.beneficiaries.length === 1) {
            if (beneficiary.familyMember) {
                const memberId = beneficiary.memberId!;
                if (!singleBeneficiaryDistributions.has(memberId)) {
                    singleBeneficiaryDistributions.set(memberId, []);
                }
                singleBeneficiaryDistributions.get(memberId)!.push(trust);
            } else if (beneficiary.customName !== "Northern Trust") {
                groupedInTrustDistributions.push([trust]);
            }
        } else {
            groupedInTrustDistributions.push([trust]);
        }
    });

    const allNTBeneficiaryDistributions = inTrustDistributions.filter(trust => trust.beneficiaries.length === 1
        && !trust.beneficiaries[0].familyMember
        && trust.beneficiaries[0].customName === "Northern Trust");
    if (allNTBeneficiaryDistributions.length > 0) {
        groupedInTrustDistributions.push(allNTBeneficiaryDistributions);
    }

    singleBeneficiaryDistributions.forEach((trusts) => {
        groupedInTrustDistributions.push(trusts);
    });

    return groupedInTrustDistributions;
};

const getTrustDistributionRoles = (trustees: Trustee[],
                                   beneficiaries: Trustee[],
                                   familyMembers: MemberGroup,): EstateCardRole[] => {
    let roles: EstateCardRole[] = [];

    roles.push({
        type: MemberType.Trustee,
        value: trustees.map(
            member => member.familyMember ? getMemberNameById(familyMembers, member.memberId!) : member.customName
        ).join(', ') ?? ""
    });

    if (beneficiaries && beneficiaries.length > 0) {
        roles.push({
            type: MemberType.Beneficiary,
            value: beneficiaries.map(
                member => member.familyMember ? getMemberNameById(familyMembers, member.memberId!) : member.customName
            ).join(', ') ?? ""
        });
    }

    return roles;
}

export const getWillFiduciaryRoles = (will: Will,
                                      familyMembers: MemberGroup,
                                      fiduciarySummary?: FiduciarySummary): EstateCardRole[] => {
    const roles: EstateCardRole[] = [];
    if (!fiduciarySummary) return roles;

    let assignment;
    if (fiduciarySummary.primaryMember && fiduciarySummary.primaryMember.assignedToMemberId === will.willOwner) {
        assignment = fiduciarySummary.primaryMember;
    } else if (fiduciarySummary.partnerMember && fiduciarySummary.partnerMember.assignedToMemberId === will.willOwner) {
        assignment = fiduciarySummary.partnerMember;
    }
    if (assignment) {
        if (assignment.executors && assignment.executors.length > 0) {
            roles.push({
                type: FiduciaryMemberType.Executor, // Use MemberType.Executor if available
                value: assignment.executors.map(
                    member => member.familyMember ? getMemberNameById(familyMembers, member.memberId!) : member.customName
                ).join(', ') ?? ""
            });
        }
        if (assignment.guardiansForMinors && assignment.guardiansForMinors.length > 0) {
            roles.push({
                type: FiduciaryMemberType.Guardian, // Use MemberType.Guardian if available
                value: assignment.guardiansForMinors.map(
                    member => member.familyMember ? getMemberNameById(familyMembers, member.memberId!) : member.customName
                ).join(', ') ?? ""
            });
        }
    }
    return roles;
}

export function getDistributionTypeFromVisualCardRepresentation(visualCardRepresentation: string): DistributionType {
    switch (visualCardRepresentation.toUpperCase()) {
        case "MISCELLANEOUS":
            return DistributionType.MISCELLANEOUS;
        case "CHARITY":
            return DistributionType.CHARITY;
        case "OUTRIGHT":
            return DistributionType.OUTRIGHT;
        case "TRUST":
            return DistributionType.TRUST;
        default:
            return DistributionType.MISCELLANEOUS;
    }
}

export const applyDagreLayout = (
    nodes: Node[],
    edges: Edge[],
    flowchartDirection: string = 'TB',
    nodeSep: number = 32,
    viewportWidth: number = 1366,
    isFirstLevelChildNodesLayout: boolean = false
) => {
    const dagre = require('dagre');
    const dagreGraph = new dagre.graphlib.Graph();

    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
        rankdir: flowchartDirection,
        nodesep: nodeSep,
        ranksep: isFirstLevelChildNodesLayout ? nodeSep : EDGE_HEIGHT
    });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, {
            width: node.width,
            height: node.height,
        });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const laidOutNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        const width = node.width!;
        const height = node.height!;
        return {
            ...node,
            position: {
                x: nodeWithPosition.x - width / 2,
                y: nodeWithPosition.y - height / 2,
            },
        };
    });

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    laidOutNodes.forEach((node) => {
        const width = node.width!;
        const height = node.height!;
        minX = Math.min(minX, node.position.x);
        maxX = Math.max(maxX, node.position.x + width);
        minY = Math.min(minY, node.position.y);
        maxY = Math.max(maxY, node.position.y + height);
    });

    const graphWidth = maxX - minX;
    const xOffset = viewportWidth / 2 - (minX + graphWidth / 2);

    if (isFirstLevelChildNodesLayout) {
        return laidOutNodes.map((node) => ({
            ...node,
            position: {
                x: node.position.x + xOffset,
                y: node.position.y + 82 // Adjusting y position to account for header height
            },
        }));
    }

    let resultNodes: Node[] = [];
    laidOutNodes.forEach((node, index) => {
        let yPosition = 0;
        if (
            node.position.y !== 0 &&
            node.type !== "firstLevelParentNode" &&
            node.type !== "estateTypeParentNode"
        ) {
            const previousNode = resultNodes[index - 1];
            yPosition = (previousNode.position.y + previousNode.height!) + EDGE_HEIGHT;
        }
        resultNodes.push({
            ...node,
            position: {
                x: node.position.x + xOffset,
                y: yPosition
            }
        });
    });

    return resultNodes;
};

export const centerSelectedCard = (
    cardDomRect: DOMRect,
    width: number,
    flowchartVisualMainRect: DOMRect,
    inputNodes: Node[]
): Node[] => {

    let xOffset = 0, yOffset = 0;
    const flowchartHeight = flowchartVisualMainRect.height;
    const flowchartLeftBoundary = flowchartVisualMainRect.left;
    const flowchartTopBoundary = flowchartVisualMainRect.top;

    const expectedCardPositionX = (width / 2) - (cardDomRect.width / 2);
    const expectedCardPositionY = (flowchartHeight / 2) - (cardDomRect.height / 2);

    if (cardDomRect.x - flowchartLeftBoundary < 0) {
        xOffset = expectedCardPositionX - (cardDomRect.x - flowchartLeftBoundary);
    } else {
        xOffset = ((cardDomRect.x - flowchartLeftBoundary) - expectedCardPositionX) * -1;
    }

    if (cardDomRect.y - flowchartTopBoundary < 0) {
        yOffset = expectedCardPositionY - (cardDomRect.y - flowchartTopBoundary);
    } else if (cardDomRect.y + cardDomRect.height > flowchartHeight) {
        yOffset = ((cardDomRect.y - flowchartTopBoundary) - expectedCardPositionY) * -1;
    }

    const centeredNodes = inputNodes;
    centeredNodes.forEach((node) => {
        if (node.type === "estateTypeParentNode") {
            node.position = {
                x: node.position.x + xOffset,
                y: node.position.y + yOffset,
            }
        }
    });

    return centeredNodes;
}
